import type { MessageType, QuickAction as AIQuickAction } from "@expert/shared-types";

import type { DefaultMessageKey } from "../../shared-types";
import type { MultipleActions, QuickAction } from "./types";

import { DEAD_AIR_TAG } from "../../shared-types";
import { useTimelineStore } from "../../state";
import { getQuickActions } from "./getQuickActions";
import { toAIQuickActions } from "./toAIQuickActions";
import { useCreateActions } from "./useCreateActions";

interface QuickActionsManagerProps {
    currentMessageType: MessageType;
    messageId?: string;
    defaultMessageKey?: DefaultMessageKey;
    tag?: string;
    isOverflowing: boolean;
    isExpanded: boolean;
    isLastBotMessage: boolean;
    isLastProactiveMessage: boolean;
    setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    aiQuickActions?: AIQuickAction[];
}

/*
Based on design, only a single quick actions group is showing on the screen at any time.
*/
export function useQuickActionsManager({
    currentMessageType,
    messageId,
    tag,
    isOverflowing,
    isExpanded,
    isLastBotMessage,
    isLastProactiveMessage,
    setIsExpanded,
    aiQuickActions,
}: QuickActionsManagerProps): QuickAction[] {
    const { currentSessionId, getTimelineState } = useTimelineStore();
    const { partner } = getTimelineState(currentSessionId);

    const allActions = useCreateActions({
        setIsExpanded,
        messageId,
        messageType: currentMessageType,
    });
    const currentActions: MultipleActions[] = [];

    const shouldShowActions = isLastBotMessage || isLastProactiveMessage;

    if (shouldShowActions) {
        switch (currentMessageType) {
            case "Search": {
                currentActions.push(...toAIQuickActions(aiQuickActions));
                if (partner === "att") {
                    currentActions.push(allActions.neutralOrPositiveFeedback, allActions.tsnr);
                }
                break;
            }
            case "Proactive": {
                const isDeadAir = tag === DEAD_AIR_TAG;
                if (isOverflowing && !isExpanded) {
                    // Feedback stays suppressed for dead air by product choice, but "Show more" is a
                    // readability affordance, not feedback - without it the tail of a long dead-air
                    // suggestion is unreachable.
                    currentActions.push(allActions.expandMessage);
                    if (!isDeadAir) {
                        currentActions.push(allActions.negativeFeedback);
                    }
                } else if (!isDeadAir) {
                    currentActions.push(allActions.neutralOrPositiveFeedback, allActions.negativeFeedback);
                }
                break;
            }
            case "SessionSummary":
                if (isOverflowing && !isExpanded) {
                    currentActions.push(allActions.expandMessage);
                }
                break;
            default:
                break;
        }
    }

    const quickActions = getQuickActions(currentActions);
    return quickActions;
}
